"use client";

import { c as _c } from "react/compiler-runtime";
import { EventEnum } from "@sprawdzonabron/shared";
import isEqual from "lodash/isEqual";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ChangeEventHandler, useEffect, useState, useTransition } from "react";
import { FiSearch, FiSettings, FiX } from "react-icons/fi";
import { TestIdEnum } from "../../../../../tests/types";
import { useAnalyticsEvent } from "../../../../api/hooks/useAnalyticsEvent";
import { getFilters } from "../../../../features/filters/utils";
import { URLParamEnum } from "../../../../features/urlParams/types";
import { selectExcludedPhrases, selectFiltersSearch, useSelector } from "../../../../store/selectors";
import { useStoreSlices } from "../../../../store/slices";
import { omitEmpty } from "../../../../utils/omitEmpty";
import { MobileSearchSuffix } from "./MobileSearchSuffix";
export const Search = () => {
  const $ = _c(78);
  const {
    updateFilters
  } = useStoreSlices();
  const search = useSelector(selectFiltersSearch);
  const excludedPhrases = useSelector(selectExcludedPhrases);
  const [searchValue, setSearchValue] = useState(search);
  const [isFocused, setIsFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [excludedPhrase, setExcludedPhrase] = useState("");
  const [excludedPhrasesInternal, setExcludedPhrasesInternal] = useState(excludedPhrases);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const postSearchEvent = useAnalyticsEvent(EventEnum.Search);
  let t0;
  let t1;
  if ($[0] !== search) {
    t0 = () => {
      setSearchValue(search);
    };
    t1 = [search];
    $[0] = search;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== excludedPhrases) {
    t2 = () => {
      setExcludedPhrasesInternal(excludedPhrases);
    };
    t3 = [excludedPhrases];
    $[3] = excludedPhrases;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsSearching(false);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== searchParams) {
    t5 = [searchParams];
    $[7] = searchParams;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== pathname) {
    t6 = pathname?.startsWith("/bron/") && pathname.split("/").filter(Boolean).length === 3;
    $[9] = pathname;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const isProductPage = t6;
  let t7;
  if ($[11] !== excludedPhrases || $[12] !== excludedPhrasesInternal || $[13] !== isProductPage || $[14] !== postSearchEvent || $[15] !== router || $[16] !== search || $[17] !== searchParams || $[18] !== searchValue || $[19] !== updateFilters) {
    t7 = () => {
      const trimmedSearch = searchValue.trim();
      if (trimmedSearch === search && isEqual(excludedPhrases, excludedPhrasesInternal)) {
        return;
      }
      setIsSearching(true);
      postSearchEvent(trimmedSearch);
      if (isProductPage) {
        const newFilters = omitEmpty(getFilters(searchParams, {
          search: trimmedSearch,
          excludedPhrases: excludedPhrasesInternal
        }));
        const newSearchParams = new URLSearchParams({
          [URLParamEnum.Filters]: JSON.stringify(newFilters)
        });
        startTransition(() => {
          router.push(`/?${newSearchParams.toString()}`, {
            scroll: true
          });
        });
      } else {
        updateFilters({
          search: trimmedSearch,
          excludedPhrases: excludedPhrasesInternal
        });
      }
    };
    $[11] = excludedPhrases;
    $[12] = excludedPhrasesInternal;
    $[13] = isProductPage;
    $[14] = postSearchEvent;
    $[15] = router;
    $[16] = search;
    $[17] = searchParams;
    $[18] = searchValue;
    $[19] = updateFilters;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleSearch = t7;
  let t8;
  if ($[21] !== handleSearch) {
    t8 = event => {
      if (event.key === "Enter") {
        handleSearch();
      }
    };
    $[21] = handleSearch;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleKeyDown = t8;
  let t9;
  if ($[23] !== excludedPhrase || $[24] !== excludedPhrasesInternal) {
    t9 = () => {
      const trimmed = excludedPhrase.trim();
      if (trimmed && !excludedPhrasesInternal.includes(trimmed)) {
        setExcludedPhrasesInternal(curr => [...curr, trimmed]);
        setExcludedPhrase("");
      }
    };
    $[23] = excludedPhrase;
    $[24] = excludedPhrasesInternal;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const handleAddExcludedPhrase = t9;
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = phrase => {
      setExcludedPhrasesInternal(curr_0 => curr_0.filter(p => p !== phrase));
    };
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleRemoveExcludedPhrase = t10;
  let t11;
  if ($[27] !== handleAddExcludedPhrase) {
    t11 = event_0 => {
      if (event_0.key === "Tab") {
        event_0.preventDefault();
        handleAddExcludedPhrase();
      }
    };
    $[27] = handleAddExcludedPhrase;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleExcludedKeyDown = t11;
  let t12;
  if ($[29] !== handleAddExcludedPhrase) {
    t12 = e => {
      const {
        value
      } = e.target;
      const lastChar = value[value.length - 1];
      if (lastChar === ",") {
        handleAddExcludedPhrase();
        return;
      }
      setExcludedPhrase(e.target.value);
    };
    $[29] = handleAddExcludedPhrase;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const onExcludedPhraseChange = t12;
  const isLoading = isSearching || isPending;
  const t13 = `absolute -inset-0.5 rounded-xl bg-gradient-to-r from-brand-400 via-teal-500 to-brand-600 transition duration-500 ${isFocused ? "opacity-75 blur" : "opacity-40 blur-sm"}`;
  let t14;
  if ($[31] !== t13) {
    t14 = <div className={t13} />;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = `w-5 h-5 transition-colors duration-300 ${isFocused ? "text-brand-500" : "text-slate-400"}`;
  let t16;
  if ($[33] !== t15) {
    t16 = <div className="pl-4 pr-2"><FiSearch className={t15} /></div>;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e_0 => setSearchValue(e_0.target.value);
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setIsFocused(true);
    t19 = () => setIsFocused(false);
    $[36] = t18;
    $[37] = t19;
  } else {
    t18 = $[36];
    t19 = $[37];
  }
  let t20;
  if ($[38] !== handleKeyDown || $[39] !== isLoading || $[40] !== searchValue) {
    t20 = <input type="text" className="w-full h-full pr-2 bg-transparent border-0 ring-0 focus:ring-0 outline-none text-slate-900 dark:text-slate-50 placeholder-slate-400 font-medium text-sm" placeholder="Szukaj broni, kalibru, miasta..." value={searchValue} onChange={t17} onKeyDown={handleKeyDown} onFocus={t18} onBlur={t19} disabled={isLoading} data-testid={TestIdEnum.SearchInput} />;
    $[38] = handleKeyDown;
    $[39] = isLoading;
    $[40] = searchValue;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== showAdvanced) {
    t21 = () => setShowAdvanced(!showAdvanced);
    $[42] = showAdvanced;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const t22 = `p-2 mr-2 rounded-lg transition-colors ${showAdvanced ? "text-brand-500 bg-brand-50 dark:bg-brand-900/20" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"}`;
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <FiSettings className="w-4 h-4" />;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t21 || $[46] !== t22) {
    t24 = <button type="button" onClick={t21} className={t22} title="Wyszukiwanie zaawansowane">{t23}</button>;
    $[45] = t21;
    $[46] = t22;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== excludedPhrasesInternal.length) {
    t25 = excludedPhrasesInternal.length > 0 && <span className="absolute top-0 right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-brand-600 rounded-full pointer-events-none">{excludedPhrasesInternal.length}</span>;
    $[48] = excludedPhrasesInternal.length;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t24 || $[51] !== t25) {
    t26 = <div className="relative">{t24}{t25}</div>;
    $[50] = t24;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const t27 = isLoading ? "Szukam..." : "Szukaj";
  let t28;
  if ($[53] !== handleSearch || $[54] !== isLoading || $[55] !== t27) {
    t28 = <button type="button" onClick={handleSearch} disabled={isLoading} data-testid={TestIdEnum.SearchButton} className="px-4 py-1.5 mr-2 bg-slate-100 dark:bg-slate-800 hover:bg-brand-500 hover:text-white rounded-lg text-slate-500 dark:text-slate-300 transition-colors text-xs font-semibold whitespace-nowrap">{t27}</button>;
    $[53] = handleSearch;
    $[54] = isLoading;
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <MobileSearchSuffix />;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t26 || $[59] !== t28) {
    t30 = <div className="pr-2 flex items-center">{t26}{t28}{t29}</div>;
    $[58] = t26;
    $[59] = t28;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t16 || $[62] !== t20 || $[63] !== t30) {
    t31 = <div className="flex items-center h-12">{t16}{t20}{t30}</div>;
    $[61] = t16;
    $[62] = t20;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== excludedPhrase || $[66] !== excludedPhrasesInternal || $[67] !== handleAddExcludedPhrase || $[68] !== handleExcludedKeyDown || $[69] !== onExcludedPhraseChange || $[70] !== showAdvanced) {
    t32 = showAdvanced && <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-800"><div className="mt-3"><label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">Wyklucz frazy (wciśnij Tab aby dodać lub użyj ",")</label><div className="flex flex-wrap gap-2 mb-2">{excludedPhrasesInternal.map(phrase_0 => <span key={phrase_0} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border border-red-100 dark:border-red-900/30">{phrase_0}<button type="button" onClick={() => handleRemoveExcludedPhrase(phrase_0)} className="ml-1.5 text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300"><FiX className="w-3 h-3" /></button></span>)}</div><div className="flex"><input type="text" className="flex-1 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="np. gen 3, gen III..." value={excludedPhrase} onChange={onExcludedPhraseChange} onKeyDown={handleExcludedKeyDown} /><button type="button" onClick={handleAddExcludedPhrase} className="ml-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-medium transition-colors">Dodaj</button></div></div></div>;
    $[65] = excludedPhrase;
    $[66] = excludedPhrasesInternal;
    $[67] = handleAddExcludedPhrase;
    $[68] = handleExcludedKeyDown;
    $[69] = onExcludedPhraseChange;
    $[70] = showAdvanced;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t31 || $[73] !== t32) {
    t33 = <div className="relative flex flex-col bg-white dark:bg-slate-900 rounded-xl shadow-lg">{t31}{t32}</div>;
    $[72] = t31;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t14 || $[76] !== t33) {
    t34 = <div className="w-full max-w-2xl mx-auto relative"><div className="relative group">{t14}{t33}</div></div>;
    $[75] = t14;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  return t34;
};